/**
 * Input sanitizers for 雇入通知書兼雇用契約書 admin fields (invite Step 2).
 * Hours use 1–12 with 午前/午後; minutes 0–59.
 */

/** Digits only, clamped to [min, max], at most maxLen characters. Empty stays empty. */
export function clampDigitInt(
  raw: string,
  min: number,
  max: number,
  maxLen: number
): string {
  const digits = raw.replace(/\D/g, '').slice(0, maxLen);
  if (!digits) return '';
  const n = Number(digits);
  if (Number.isNaN(n)) return '';
  if (n > max) return String(max);
  if (n < min) return '';
  return digits;
}

/** Clock hour with 午前/午後: 1–12. */
export function sanitizeHour12(raw: string): string {
  return clampDigitInt(raw, 1, 12, 2);
}

/** Minutes: 0–59. */
export function sanitizeMinute(raw: string): string {
  return clampDigitInt(raw, 0, 59, 2);
}

/** Break length in minutes: 0–999. */
export function sanitizeBreakMinutes(raw: string): string {
  return clampDigitInt(raw, 0, 999, 3);
}

/**
 * Daily work hours (実働1日): non-negative decimal up to 24, at most one fractional digit.
 * Examples: "", "8", "8.5", "24"
 */
export function sanitizeDailyWorkHours(raw: string): string {
  let s = raw.replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  if (s === '' || s === '.') return s === '.' ? '0.' : '';

  const parts = s.split('.');
  let intPart = parts[0] ?? '';
  let fracPart = parts[1];

  if (intPart.length > 2) intPart = intPart.slice(0, 2);
  if (fracPart !== undefined) fracPart = fracPart.slice(0, 1);

  const composed =
    fracPart !== undefined ? `${intPart}.${fracPart}` : intPart;

  if (composed === '' || composed === '.') return composed === '.' ? '0.' : '';
  if (composed.endsWith('.')) {
    const head = Number(composed.slice(0, -1) || '0');
    if (head > 24) return '24';
    return composed;
  }

  const n = Number(composed);
  if (Number.isNaN(n)) return '';
  if (n > 24) return '24';
  return composed;
}

/** Yen amount: digits only, max 9 digits. */
export function sanitizeYenAmount(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

/** Compensation days: 0–99. */
export function sanitizeCompensationDays(raw: string): string {
  return clampDigitInt(raw, 0, 99, 2);
}

/** Overtime threshold hours (h以内 / h超): 0–99. */
export function sanitizeOvertimeHours(raw: string): string {
  return clampDigitInt(raw, 0, 99, 2);
}

/** Premium rate %: 0–999. */
export function sanitizePercentRate(raw: string): string {
  return clampDigitInt(raw, 0, 999, 3);
}

/** Plain text with a max length (trim not applied — preserves typing spaces). */
export function sanitizeText(raw: string, maxLen: number): string {
  if (raw.length <= maxLen) return raw;
  return raw.slice(0, maxLen);
}

/**
 * Person name (e.g. 使用者 所長): letters (incl. kana/kanji/Latin), spaces,
 * and common name marks (・ーｰ). Digits and other symbols stripped.
 */
export function sanitizePersonName(raw: string, maxLen: number): string {
  const cleaned = raw.replace(/[^\p{L}\p{M}\s・ーｰ]/gu, '');
  return sanitizeText(cleaned, maxLen);
}

export const EMP_CONTRACT_TEXT_LIMITS = {
  workplacePrefectureName: 12,
  workplaceCityName: 40,
  workplaceSite: 60,
  jobDescription: 500,
  termReason: 120,
  allowances: 200,
  siteManagerName: 40,
} as const;
